import classNames from 'classnames';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { WarningMessage } from '../../../../components/widgets';
import { getSectionAccessType } from '../../../../selectors/navigation';
import { SECTION_ACCESS_TYPES, SECTIONS } from '../../../../utils/consts';
import {
    eventShape,
    organizationOptionsShape,
    partnerShape,
    siteShape,
    templateShape,
    TemplateTypes,
} from '../../types';
import { POSTBACK_FILTERS_TYPES } from '../advancedPostbackSettings/PostbackFilters';
import css from '../PartnerConfigurationShelf.css';
import eventPostbackCss from './CustomEventPostbacks.css';
import EventPostbacksData from './EventPostbacksData';

export const EVENTS_TABLE_DATA_TEST_ID = 'events-postbacks-table';

/**
 * Merge configured events with allowed events for display.
 * Configured events not in allowed list are marked as disallowed.
 */
export const mergeEventsWithConfigured = (allowedEvents, configuredPostbacks, isRevenueEvent = false) => {
    if (!configuredPostbacks?.length) return allowedEvents;

    const allowedEventIds = new Set(allowedEvents.map(e => e.id));
    const configuredOnlyEvents = configuredPostbacks
        .filter(postback => postback.eventId && !allowedEventIds.has(postback.eventId))
        .map(postback => ({
            id: postback.eventId,
            eventName: postback.eventName,
            displayName: postback.eventName,
            revenueEvent: isRevenueEvent,
            _disallowed: true,
        }));

    return [...allowedEvents, ...configuredOnlyEvents];
};

function CustomEventPostbacks({
    partner,
    site,
    templates = [],
    events = [],
    isReadonly = false,
    orgOptions,
    availableFilters,
}) {
    const sectionAccessType = useSelector(
        getSectionAccessType('new_partner_config', SECTIONS.PARTNER_CONFIGURATION_IN_APP_EVENTS_POSTBACK)
    );
    const hasViewAccess = sectionAccessType === SECTION_ACCESS_TYPES.VIEW;
    const disabled = hasViewAccess || isReadonly;
    const { values, setValues, errors, touched, setTouched } = useFormikContext();

    // Filter to custom (non-revenue) events from allowed events
    const allowedCustomEvents = events.filter(event => !event.revenueEvent);
    const customEventTemplate = templates.find(template => template.type === TemplateTypes.CUSTOM);

    // Merge configured events with allowed events - configured events not in allowed list are marked as _disallowed
    const customEvents = useMemo(
        () => mergeEventsWithConfigured(allowedCustomEvents, values.customEventPostbacks, false),
        [allowedCustomEvents, values.customEventPostbacks]
    );

    const addEmptyEventPostback = () => setValues({ ...values, customEventPostbacks: [] });
    const setEventPostbacksValues = customEventPostbacks => setValues({ ...values, customEventPostbacks });
    const setTouchedEventsPostbacks = customEventPostbacks => setTouched({ ...touched, customEventPostbacks });

    return (
        <div
            className={css.section}
            data-field-error={errors.customEventPostbacks ? 'customEventPostbacks' : undefined}
        >
            <div className={css.title}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.EVENTS_POSTBACKS" />
            </div>
            {hasViewAccess && !isReadonly && (
                <WarningMessage
                    showIcon={false}
                    showSpacer={false}
                    type="permission"
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.VIEW_ACCESS_ONLY"
                    className={css.viewAccessMessage}
                    textContainerClass={css.viewAccessMessageTextContainer}
                />
            )}
            {values.customEventPostbacks && customEventTemplate && customEvents.length ? null : (
                <div
                    onClick={addEmptyEventPostback}
                    className={classNames(eventPostbackCss.addEventButton, {
                        // Use allowedCustomEvents for checking if we can add new events
                        [eventPostbackCss.disabled]: !customEventTemplate || !allowedCustomEvents.length || disabled,
                    })}
                >
                    <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.ADD_EVENT" />
                </div>
            )}
            <EventPostbacksData
                configuredEvents={values.customEventPostbacks}
                template={customEventTemplate}
                events={customEvents}
                allowedEvents={allowedCustomEvents}
                partner={partner}
                site={site}
                tableDataTestId={EVENTS_TABLE_DATA_TEST_ID}
                templateType={TemplateTypes.CUSTOM}
                setPostbacks={setEventPostbacksValues}
                setTouched={setTouchedEventsPostbacks}
                errors={errors.customEventPostbacks}
                touched={touched.customEventPostbacks}
                isReadonly={disabled}
                orgOptions={orgOptions}
                availableFilters={availableFilters}
            />
        </div>
    );
}

CustomEventPostbacks.propTypes = {
    partner: partnerShape.isRequired,
    site: siteShape.isRequired,
    orgOptions: organizationOptionsShape.isRequired,
    templates: PropTypes.arrayOf(templateShape),
    events: PropTypes.arrayOf(eventShape),
    isReadonly: PropTypes.bool,
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
};

export default CustomEventPostbacks;
